/**
 * DailyFocusView Component
 * Dedicated workspace for daily planning and focused execution.
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, CheckCircle2, Circle, ArrowRight } from 'lucide-react';

export function DailyFocusView({
  quadrants,
  onToggleTask,
  onTogglePlanToday,
  onBackToMatrix,
  todayStr,
}) {
  // Collect all tasks planned for today or completed today
  const todayTasks = [];
  quadrants.forEach((q) => {
    (q.tasks || []).forEach((t) => {
      if (t.plannedDate === todayStr || (t.completed && t.completedAt?.startsWith(todayStr))) {
        todayTasks.push({ ...t, quadrantId: q.id, quadrantTitle: q.title, color: q.color });
      }
    });
  });

  const completedCount = todayTasks.filter((t) => t.completed).length;
  const progressPercent = todayTasks.length > 0
    ? Math.round((completedCount / todayTasks.length) * 100)
    : 0;

  return (
    <div className="daily-focus-container">
      {/* Header Bar */}
      <div className="daily-focus-header">
        <div className="focus-title-group">
          <Sun size={24} className="sun-icon text-amber-500" />
          <div>
            <h2>Kế Hoạch Hôm Nay</h2>
            <p className="focus-subtitle">Tập trung hoàn thành các mục tiêu quan trọng nhất trong ngày</p>
          </div>
        </div>

        <button className="back-matrix-btn" onClick={onBackToMatrix}>
          <span>Về Ma Trận</span>
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Daily Progress Card */}
      <div className="daily-progress-card">
        <div className="progress-card-info">
          <span>Tiến độ ngày hôm nay</span>
          <strong>{completedCount} / {todayTasks.length} hoàn thành ({progressPercent}%)</strong>
        </div>
        <div className="daily-progress-track">
          <motion.div
            className="daily-progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {/* Task List for Today */}
      <div className="daily-task-list">
        {todayTasks.length === 0 ? (
          <div className="daily-empty-state">
            <Sun size={48} className="empty-sun-icon" />
            <h3>Chưa có nhiệm vụ nào được lên lịch hôm nay</h3>
            <p>Hãy chuyển sang Ma Trận và nhấn biểu tượng ☀️ trên các task quan trọng để đưa vào kế hoạch hôm nay!</p>
            <button className="empty-action-btn" onClick={onBackToMatrix}>
              Chọn nhiệm vụ từ Ma Trận
            </button>
          </div>
        ) : (
          <AnimatePresence>
            {todayTasks.map((task) => (
              <motion.div
                key={task.id}
                layout
                className={`daily-task-item ${task.completed ? 'completed' : ''} ${task.color}`}
              >
                <button
                  className="daily-task-checkbox"
                  onClick={() => onToggleTask(task.quadrantId, task.id)}
                  aria-label={task.completed ? 'Đánh dấu chưa xong' : 'Đánh dấu đã xong'}
                >
                  {task.completed ? (
                    <CheckCircle2 size={18} className="text-emerald-500" />
                  ) : (
                    <Circle size={18} className="text-slate-400" />
                  )}
                </button>

                <div className="daily-task-content">
                  <span className="daily-task-text">{task.text}</span>
                  <span className="daily-task-quadrant-tag">{task.quadrantTitle}</span>
                </div>

                <button
                  className="remove-from-today-btn"
                  onClick={() => onTogglePlanToday(task.quadrantId, task.id)}
                  title="Bỏ khỏi kế hoạch hôm nay"
                >
                  ✕
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
